import { useEffect, useState } from "react";
import api from "@/lib/api";
import EditorLayout from "@/components/editor/EditorLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, UserPlus, Trash2, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";

export default function AdminsPage() {
  const [admins, setAdmins] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/admins").then((r) => setAdmins(r.data)).catch(() => setAdmins([]));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("כתובת מייל לא תקינה"); return; }
    setAdding(true);
    try {
      const r = await api.post("/admins", { email, name });
      toast.success(r.data.already ? "המנהל כבר קיים ברשימה" : "המנהל נוסף");
      setEmail(""); setName(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "ההוספה נכשלה"); }
    finally { setAdding(false); }
  };

  const remove = async () => {
    try { await api.delete(`/admins/${delTarget.id}`); toast.success("ההרשאה הוסרה"); setDelTarget(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "ההסרה נכשלה"); setDelTarget(null); }
  };

  return (
    <EditorLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">מורשי גישה</h1>
        <p className="text-[#5C635C] mt-1">מי יכול להיכנס ולערוך את מערכת הניהול.</p>
      </div>

      <div className="flex items-start gap-2 bg-[#F2F5F1] rounded-lg p-4 text-sm text-[#5C635C] mb-6">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#4A5D4E]" />
        רק כתובות Google שברשימה הזו יכולות להתחבר לממשק הניהול. הוסיפי כאן את המייל של כל מי שאמור לערוך את האתר.
      </div>

      <form onSubmit={add} className="bg-white border border-[#E5E2D9] rounded-2xl p-6 mb-6 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className="block text-sm font-semibold mb-1.5">כתובת Gmail *</label>
          <Input dir="ltr" data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@gmail.com" />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-semibold mb-1.5">שם (אופציונלי)</label>
          <Input data-testid="admin-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="לדוגמה: רימונה" />
        </div>
        <Button type="submit" data-testid="admin-add-btn" disabled={adding} className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full gap-2">
          <UserPlus className="w-4 h-4" /> {adding ? "מוסיף..." : "הוספה"}
        </Button>
      </form>

      {admins === null ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#4A5D4E] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {admins.map((a) => (
            <div key={a.id} data-testid={`admin-row-${a.id}`} className="bg-white border border-[#E5E2D9] rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-[#F2F5F1] flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-[#4A5D4E]" /></div>
                <div className="min-w-0">
                  <p className="font-semibold flex items-center gap-2">{a.name || a.email.split("@")[0]} {a.is_self && <span className="text-xs bg-[#F2F5F1] text-[#4A5D4E] px-2 py-0.5 rounded-full">את/ה</span>}</p>
                  <p className="text-sm text-[#8a8f89]" dir="ltr">{a.email}</p>
                </div>
              </div>
              <button onClick={() => setDelTarget(a)} disabled={a.is_self} data-testid={`admin-remove-${a.id}`}
                      className="p-2 rounded-lg text-[#8C3A3A] hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent dir="rtl" className="font-editor" data-testid="admin-delete-dialog">
          <DialogHeader><DialogTitle className="text-right flex items-center gap-2 text-[#8C3A3A]"><AlertTriangle className="w-5 h-5" /> הסרת מורשה גישה</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5C635C]">הכתובת <b dir="ltr">{delTarget?.email}</b> לא תוכל יותר להיכנס למערכת הניהול. אפשר להוסיף אותה שוב בכל עת.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelTarget(null)} className="rounded-full">ביטול</Button>
            <Button data-testid="admin-delete-confirm" onClick={remove} className="bg-[#8C3A3A] hover:bg-[#742f2f] rounded-full">הסרה</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EditorLayout>
  );
}
